import React from 'react';
import { render, screen } from '@testing-library/react';
import userEvent from '@testing-library/user-event';

import ConfigProvider from 'components/ConfigProvider';

import AccountAccessTable from './AccountAccessTable';

const ACTIVE_ROW = {
  id: 1,
  decisivId: 'ACCOUNTA',
  name: 'Account A',
  dealerType: 'FLEET',
  roles: ['Fleet Administrator'],
  removedAt: null,
};

const MULTI_ROLE_ROW = {
  id: 2,
  decisivId: 'ACCOUNTB',
  name: 'Account B',
  dealerType: 'MEMBER',
  roles: ['Member', 'Manager'],
  removedAt: null,
};

const REMOVED_ROW = {
  id: 3,
  decisivId: 'ACCOUNTC',
  name: 'Account C',
  dealerType: 'BROKER',
  roles: ['Member'],
  removedAt: '2025-01-01T00:00:00Z',
};

const renderTable = (props = {}) =>
  render(
    <ConfigProvider>
      <AccountAccessTable
        canActOn={() => true}
        canEdit
        onRemove={jest.fn()}
        onRestore={jest.fn()}
        accounts={[ACTIVE_ROW]}
        {...props}
      />
    </ConfigProvider>,
  );

describe('management/EditUser AccountAccessTable', () => {
  it('titles the section', () => {
    renderTable();

    expect(
      screen.getByRole('heading', { name: 'Accounts' }),
    ).toBeInTheDocument();
  });

  it('shows the account name', () => {
    renderTable();

    expect(screen.getByText('Account A')).toBeInTheDocument();
  });

  it('shows the account id and type on one line', () => {
    renderTable();

    expect(
      screen.getByText('Account ID: ACCOUNTA · Account Type: FLEET'),
    ).toBeInTheDocument();
  });

  it('shows a role tag', () => {
    renderTable();

    expect(screen.getByText('Fleet Administrator')).toBeInTheDocument();
  });

  it('shows one tag per role when an account has more than one', () => {
    renderTable({ accounts: [MULTI_ROLE_ROW] });

    expect(screen.getByText('Member')).toBeInTheDocument();
    expect(screen.getByText('Manager')).toBeInTheDocument();
  });

  describe('an active account', () => {
    it('offers a Remove Access button', () => {
      renderTable();

      expect(
        screen.getByRole('button', { name: 'Remove Access' }),
      ).toBeInTheDocument();
    });

    it('disables Remove when canActOn says no', () => {
      renderTable({ canActOn: () => false });

      expect(
        screen.getByRole('button', { name: 'Remove Access' }),
      ).toBeDisabled();
    });

    it('calls onRemove with the row', () => {
      const onRemove = jest.fn();
      renderTable({ onRemove });

      userEvent.click(screen.getByRole('button', { name: 'Remove Access' }));

      expect(onRemove).toHaveBeenCalledWith(ACTIVE_ROW);
    });
  });

  describe('a removed account', () => {
    it('offers an always-enabled Restore Access button', () => {
      renderTable({ canActOn: () => false, accounts: [REMOVED_ROW] });

      expect(
        screen.getByRole('button', { name: 'Restore Access' }),
      ).not.toBeDisabled();
    });

    it('calls onRestore with the row', () => {
      const onRestore = jest.fn();
      renderTable({ onRestore, accounts: [REMOVED_ROW] });

      userEvent.click(screen.getByRole('button', { name: 'Restore Access' }));

      expect(onRestore).toHaveBeenCalledWith(REMOVED_ROW);
    });
  });

  describe('Add To Account', () => {
    it('offers it, enabled', () => {
      renderTable();

      expect(
        screen.getByRole('button', { name: 'Add To Account' }),
      ).not.toBeDisabled();
    });

    it('calls onAdd', () => {
      const onAdd = jest.fn();
      renderTable({ onAdd });

      userEvent.click(screen.getByRole('button', { name: 'Add To Account' }));

      expect(onAdd).toHaveBeenCalled();
    });
  });

  describe('when the viewer may not edit', () => {
    it('offers no action buttons', () => {
      renderTable({ canEdit: false });

      expect(
        screen.queryByRole('button', { name: /Access/ }),
      ).not.toBeInTheDocument();
    });

    it('offers no Add To Account button', () => {
      renderTable({ canEdit: false });

      expect(
        screen.queryByRole('button', { name: 'Add To Account' }),
      ).not.toBeInTheDocument();
    });
  });
});
